import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { copyObject } from 'common/copyObject';
import { useStore } from 'store/StoreContext';
import { storeMailAddressGenerator } from 'store/register-user/RegisterUserData';
import { callCheckMailAddressDuplicateApi, callSendOnetimePasswordMailApi } from 'requests/RequestUser';
import ReCAPTCHA from 'react-google-recaptcha';
import { useReceiveAuthorizeRequest } from 'hooks/UseReceiveAuthorizeRequest';
import { RoutePath } from 'consts/RoutePath';
import { isDevelop } from 'consts/Env';
import classes from './override.module.scss';
import { CircularProgress } from '@material-ui/core';
import { Validator } from 'validators/Validator';
import { HttpStatus } from 'consts/HttpStatus';
import { callCheckWhiteListApi } from 'requests/RequestWhiteList';
import { convertFullToHalf } from 'common/CharacterConverter';
import { ReactError } from 'consts/ReactError';

type Props = {};

export const UserRegister1Template = memo((_props: Props) => {
	const history = useHistory();
	const [store, dispatch] = useStore();
	const { isV1ParamsOk, isV2ParamsOk, burl, bsn, scd, redirect_uri, client_id } = useReceiveAuthorizeRequest(dispatch);
	const [formData, setFormData] = useState({ ...copyObject(store.registerUserData), isV1ParamsOk, isV2ParamsOk });
	const [formErrors, setFormErrors] = useState({ isNotRobot: '', mailaddress_1: '' });
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isCompleteButtonDisabled, setIsCompleteButtonDisabled] = useState(!isDevelop);
	const [processLoading, setProcessLoading] = useState(false);

	useEffect(() => {
		const execute = async () => {
			// パラメータが飛んでこなかった場合はスルー
			if (!isV1ParamsOk && !isV2ParamsOk) {
				return;
			}

			setProcessLoading(true);
			if (isV1ParamsOk) {
				const result = await callCheckWhiteListApi({
					burl,
					bsn,
					scd,
				});
				if (result.status === HttpStatus.REDIRECT) {
					history.push(RoutePath.ERROR);
					return;
				}
				setProcessLoading(false);
				return;
			}
			if (isV2ParamsOk) {
				const result = await callCheckWhiteListApi({
					burl: redirect_uri,
					scd: client_id,
				});
				if (result.status === HttpStatus.REDIRECT) {
					history.push(RoutePath.ERROR);
					return;
				}
				setProcessLoading(false);
				return;
			}
		};
		execute();
	}, [isV1ParamsOk, isV2ParamsOk]);

	// recaptcha
	const recaptchaRef = useRef(null);
	const onRecaptchaChange = (value) => {
		const isError = value == null || value === '';
		setIsCompleteButtonDisabled(true);
		if (!isError) {
			setFormErrors({ ...formErrors, isNotRobot: '' });
			setIsCompleteButtonDisabled(false);
		}
	};

	// メールアドレス入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({ ...formData, [event.target.name]: event.target.value });
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};

	// 仮登録メールを送信ボタンのonClickで呼ぶ
	const sendMail = async () => {
		setProcessLoading(true);

		setFormErrors((prev) => ({ ...prev, mailaddress_1: '' }));
		setFormErrors((prev) => ({ ...prev, isNotRobot: '' }));
		setActionError('');

		// メールアドレス確認
		const validator = new Validator(formData.mailaddress_1);
		const mailaddressError = validator.require().charTypeEmail().end();
		if (!!mailaddressError) {
			setFormErrors((prev) => ({ ...prev, mailaddress_1: mailaddressError }));
			setProcessLoading(false);
			return;
		}

		// ロボット確認
		if (!isDevelop) {
			const recaptchaValue = recaptchaRef.current.getValue();
			const isError = recaptchaValue == null || recaptchaValue === '';
			if (isError) {
				setFormErrors((prev) => ({ ...prev, isNotRobot: '「私はロボットではありません」にチェックをしてください。' }));
				setProcessLoading(false);
				return;
			}
		}

		// メールアドレス重複確認
		const checkMailResult = await callCheckMailAddressDuplicateApi({
			mailaddress: formData.mailaddress_1,
		});
		if (checkMailResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}
		if (checkMailResult.data.exist) {
			setFormErrors((prev) => ({ ...prev, mailaddress_1: '既に登録されているメールアドレスです。' }));
			setProcessLoading(false);
			return;
		}

		// ストアに格納
		storeMailAddressGenerator(dispatch, formData);

		// メール送信API
		const sendMailResult = await callSendOnetimePasswordMailApi({
			mailaddress: formData.mailaddress_1,
			send_type: 1,
		});
		if (sendMailResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}

		// 次のページへ
		history.push(RoutePath.USER_REGISTER_2);
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜仮会員登録';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-01.svg" alt="仮会員登録" />
						<img className="sp" src="images/img-steps-01_sp.svg" alt="" />
					</div>
					<div className="step-title sp">仮会員登録</div>
					<p>
						メールアドレスの仮登録を行います。入力後「メール送信」ボタンを押してください。
						<br />
						ご登録のメールアドレス宛にGakkenID事務局から、ご本人様確認の仮会員登録メールが届きます。
					</p>
					<ul className="annotation">
						<li>
							※登録を行うことで、
							<a className="text-link" href="/agreement" target="_blank">
								利用規約
							</a>
							に同意したとみなします。
						</li>
						<li>
							※メール受信制限をされている方は <span aria-hidden="true">@gakken.co.jp</span>
							<span className="visually-hidden">アットガッケンドットシーオードットジェーピー</span>{' '}
							からのメール受信を許可してください。
						</li>
					</ul>

					{isFormError || isActionError ? (
						<div className="error-box" ref={errorRef}>
							{isActionError ? <p>{actionError}</p> : null}
							{isFormError ? (
								<>
									<p>※下記項目は全て必須となります。</p>
									<div className="error-box-in">
										{formErrors.mailaddress_1 !== '' ? <p>【メールアドレス】{formErrors.mailaddress_1}</p> : null}
										{formErrors.isNotRobot !== '' ? <p>【ロボット確認】{formErrors.isNotRobot}</p> : null}
									</div>
								</>
							) : null}
						</div>
					) : null}

					<div className="form-box">
						<div className="form-head">
							<label htmlFor="mailaddress_1">仮登録のメールアドレスを入力してください。</label>
						</div>
						<div className="form-input">
							<input
								id="mailaddress_1"
								type="text"
								name="mailaddress_1"
								value={formData.mailaddress_1}
								onChange={handleChangeValue}
								onBlur={handleBlurConvertFullToHalfValue}
							/>
						</div>
						<p className="error">{formErrors.mailaddress_1}</p>
					</div>

					{!isDevelop ? (
						<div className="recaptcha-box">
							<p>機械による不正登録を防ぐために『私はロボットではありません』にチェックをしてください。</p>
							<div className="recaptcha">
								<ReCAPTCHA
									ref={recaptchaRef}
									onChange={onRecaptchaChange}
									sitekey={process.env.REACT_APP_RECAPTCHA_DATA_SITEKEY}
								/>
							</div>
						</div>
					) : null}

					<div className="btn-box">
						<div className="btn gr">
							{processLoading ? (
								<CircularProgress />
							) : (
								<button
									className={isCompleteButtonDisabled ? classes.disabled : ''}
									onClick={sendMail}
									disabled={isCompleteButtonDisabled}
								>
									メール送信
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</main>
	);
});
